import {
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  IndeterminateCheckBoxRounded,
  KeyboardArrowDownOutlined,
  Close,
} from '@mui/icons-material';
import { createTheme, IconButton } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { timesheetStatuses } from './pages/timesheets/constants';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 360,
      sm: 640,
      md: 768,
      lg: 1368,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: 'Assistant',
    h1: {
      fontSize: 48,
      fontWeight: 700,
    },
    h2: {
      fontSize: 40,
      fontWeight: 700,
    },
    h3: {
      fontSize: 32,
      fontWeight: 700,
    },
    h4: {
      fontSize: 28,
      fontWeight: 700,
    },
    h5: {
      fontSize: 24,
      fontWeight: 600,
    },
    h6: {
      fontSize: 20,
      fontWeight: 600,
    },
    body1: {
      fontSize: 18,
      fontWeight: 400,
    },
    body2: {
      fontSize: 16,
      fontWeight: 400,
    },
    subtitle1: {
      fontSize: 14,
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: 12,
      fontWeight: 400,
    },
  },
  palette: {
    primary: {
      light: '#81377A',
      main: '#0B406B',
      dark: '#36213E',
    },
    secondary: {
      main: '#0096FB',
      dark: 'var(--blue_dark)',
    },
    error: {
      main: '#BC0E33',
    },
    warning: {
      main: '#D39F06',
    },
    success: {
      main: '#6CBA60',
    },
    text: {
      primary: '#2E282A',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          zIndex: 1000,
          background: 'var(--white)',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'var(--stroke_secondary)',
        },
        root: {
          borderRadius: 10,

          '&.Mui-disabled': {
            background: 'var(--bg_disabled)',

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--stroke_secondary)',
            },
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--cta_tertiary)',
            borderWidth: 1,
          },
          '&.MuiInputBase-multiline': {
            padding: 0,
          },
          '& .MuiInputAdornment-root': {
            color: 'var(--icons_primary)',

            '& .MuiSvgIcon-root, & .MuiTypography-root': {
              color: 'var(--icons_primary)',
            },
          },
          '& .MuiIconButton-root': {
            color: 'var(--icons_primary)',
          },

          '& .MuiSvgIcon-root': {
            color: 'var(--icons_primary)',
          },
        },
        adornedStart: {
          color: 'var(--disabled)',
        },
        input: {
          color: 'var(--text_primary)',
          '.date-text': {
            color: 'var(--black_secondary)',
          },
          '&.Mui-disabled': {
            WebkitTextFillColor: 'var(--text_secondary)',
            color: 'var(--text_secondary)',
            opacity: 1,
          },
          padding: '8px 12px',
          height: '24px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 16,
          '&.MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline,:hover .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'var(--cta_tertiary)',
                borderWidth: 1,
              },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--stroke_secondary)',
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          marginBottom: 8,
          lineHeight: '18px',
          color: 'var(--black_secondary)',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
          borderRadius: 8,
        },
      },
    },

    MuiPickersDay: {
      styleOverrides: {
        root: {
          marginTop: '5px',
          fontSize: '14px',
          color: 'var(--text_primary)',
          '&&.Mui-selected': {
            backgroundColor: 'var(--cta_primary)',
          },
          '&&.MuiPickersDay-today:not(.Mui-selected)': {
            border: '1px solid var(--cta_primary)',
            color: 'var(--cta_primary)',
          },
          '&&.Mui-disabled': {
            backgroundColor: 'var(--white)',
            color: 'var( --text_disabled)',
          },
        },
      },
    },
    MuiDayPicker: {
      styleOverrides: {
        header: {
          marginTop: '5px',
        },
        weekDayLabel: {
          color: 'var(--text_tertiary)',
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },

    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          '& .MuiPickersArrowSwitcher-button:not(.Mui-disabled), & .MuiPickersCalendarHeader-switchViewButton:not(.Mui-disabled)':
            {
              color: 'var(--black_secondary)',
            },

          '& .MuiPickersArrowSwitcher-button.Mui-disabled, & .MuiPickersCalendarHeader-switchViewButton.Mui-disabled':
            {
              color: 'var(--icons_disabled)',
            },

          marginBottom: '15px',
          position: 'relative',

          '&::after': {
            content: '""',
            position: 'absolute',
            left: 5,
            right: 5,
            bottom: -10,
            height: '1px',
            backgroundColor: 'var(--stroke_tertiary)',
          },
        },
        label: ({ theme }) => ({
          ...theme.typography.body2,
          color: 'var(--text_primary)',
          fontWeight: 600,
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          fontSize: 16,
          cursor: 'pointer',
          '&.menu': {
            color: '#FFFFFF',
            paddingTop: 6,
            paddingBottom: 6,
          },
          '&.active': {
            backgroundColor: '#ffffff',
            color: '#2E282A',
            fontWeight: 700,
            '.menu-icon path': {
              fill: '#2E282A',
            },
            svg: {
              color: 'var(--icons_primary)',
            },
          },
          '&.sideListItem': {
            marginBottom: 8,
            '&.standard': {
              color: 'var(--primary_dark)',
              fontWeight: 700,
              '&.selected': {
                backgroundColor: 'var(--bg_primary)',
              },
            },
            '&.secondary': {
              color: 'var(--primary_dark)',
              paddingLeft: 32,
              '&.selected': {
                backgroundColor: 'var(--primary_25)',
                fontWeight: 700,
              },
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: 'var(--box-shadow_xl)',
          maxHeight: '320px',
          overflowY: 'auto',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownOutlined,
        MenuProps: {
          disableAutoFocusItem: true,
        },
      },
      styleOverrides: {
        icon: {
          color: 'var(--icons_primary)',
          fontSize: 24,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: 12,
          paddingBottom: 12,
          '&.Mui-selected': {
            color: 'var(--white)',
            backgroundColor: 'var(--cta_secondary)',
            '&:hover': {
              backgroundColor: 'var(--primary_main)',
            },
            '& .MuiTypography-root': {
              color: 'inherit',
            },
          },
          '&:hover': {
            backgroundColor: 'var(--bg_secondary)',
          },
          '.date-text': {
            color: 'var(--black_secondary)',
          },
          // Default text
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontWeight: 'inherit',
          '&.headings_h7': {
            fontSize: 16,
            fontWeight: 600,
          },
          '&.body_b3': {
            fontSize: 14,
            fontWeight: 400,
          },
          '&.body_b4': {
            fontSize: '12px !important',
            fontWeight: 400,
          },
          '&.body_b5': {
            fontSize: 18,
            fontWeight: 600,
          },
          '&.body_b6': {
            fontSize: 16,
            fontWeight: 600,
          },
          '&.body_b7': {
            fontSize: 14,
            fontWeight: 600,
          },
          '&.body_b8': {
            fontSize: 12,
            fontWeight: 600,
          },
          '&.subtitle_s3': {
            fontSize: '10px !important',
            fontWeight: 400,
          },
          '&.subtitle_s4': {
            fontSize: 8,
            fontWeight: 400,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          flex: 1,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'medium',
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: 14,
          fontWeight: 600,
          textTransform: 'capitalize',
          boxShadow: 'none',
        },
        outlinedPrimary: {
          border: '1px solid var(--primary_main)',
          color: 'var(--primary_main)',
          '&:disabled': {
            border: '1px solid var(--cta_disabled)',
            color: 'var(--cta_disabled)',
          },
          ':hover': {
            border: '1px solid var(--cta_secondary)',
            color: 'var(--cta_secondary)',
          },
        },
        outlinedError: {
          border: '1px solid var(--state_error)',
          color: 'var(--state_error)',
          '&:disabled': {
            border: '1px solid var(--red_light)',
            color: 'var(--red_light)',
          },
          ':hover': {
            border: '1px solid var(--error_secondary)',
            color: 'var(--error_secondary)',
          },
        },
        text: {
          color: 'var(--link_primary)',
          fontWeight: 600,
          fontSize: 14,
          padding: 4,
          ':hover': {
            backgroundColor: 'var(--link_button_bg)',
          },
          '&:disabled': {
            color: 'var(--link_disabled)',
          },
        },
        sizeLarge: {
          fontSize: 18,
          lineHeight: 1,
          padding: '12px 16px',
        },
        sizeMedium: {
          lineHeight: '18px',
          padding: '8px 12px',
        },
        sizeSmall: {
          padding: 4,
        },
        contained: {
          ':hover': {
            background: 'var(--cta_secondary)',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: (
          <IconButton>
            <CheckBoxOutlineBlankRounded
              sx={{
                fontSize: 20,
                color: 'var(--primary_main)',
                '.Mui-disabled &': {
                  color: 'var(--cta_disabled)',
                },
              }}
            />
          </IconButton>
        ),
        checkedIcon: (
          <IconButton>
            <CheckBoxRounded
              sx={{
                fontSize: 20,
                color: 'var(--primary_main)',
                '.Mui-disabled &': {
                  color: 'var(--cta_disabled)',
                },
              }}
            />
          </IconButton>
        ),
        indeterminateIcon: (
          <IconButton>
            <IndeterminateCheckBoxRounded
              sx={{
                fontSize: 20,
                color: 'var(--primary_main)',
                '.Mui-disabled &': {
                  color: 'var(--cta_disabled)',
                },
              }}
            />
          </IconButton>
        ),
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          width: 40,
          height: 40,
          color: 'var(--primary_main)',
          '&:not(.Mui-disabled):not(.Mui-checked):hover': {
            '& .MuiSvgIcon-root': {
              color: 'var(--cta_secondary)',
            },
          },
          '.MuiSvgIcon-root path': {
            borderWidth: 1,
            borderRadius: 2,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          zIndex: 999,
          border: 'none',
          '&.navigation-drawer': {
            backgroundColor: 'var(--primary_main)',
          },
          '&.custom-drawer': {
            border: '1px solid',
            borderColor: 'var(--stroke_light)',
            boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.12), 0px 20px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: 8,
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          marginLeft: 20,
        },
        badge: {
          '&.limit-badge': {
            width: 23,
            height: 18,
            background: 'var(--primary_10)',
            color: 'var(--black)',
            marginRight: 10,
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: 'var(--ivory_25)',
        },
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {},
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 44,
          borderBottom: '1px solid var(--stroke_tertiary)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
        },
        root: {
          '&.cell-bold': {
            fontSize: 14,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: 'var(--text_primary)',
        },
        selectLabel: {
          fontSize: 14,
          fontWeight: 600,
        },
        selectRoot: {
          fontSize: 14,
          fontWeight: 600,
        },
        actions: {
          '& .MuiIconButton-root': {
            color: 'var(--icons_primary)',
            '&.Mui-disabled': {
              color: 'var(--icons_disabled)',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          boxShadow: 'var(--header_drop)',
          borderRadius: 4,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 'unset',
          minWidth: 'fit-content',
          color: 'var(--black_75)',
          textTransform: 'capitalize',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          fontSize: 16,
          '&.dark': {
            background: 'var(--bg_secondary)',
            borderRadius: 12,
            padding: 4,
            '.MuiSvgIcon-root': {
              color: 'var(--icons_disabled)',
            },
            '.MuiTabs-indicator': {
              backgroundColor: 'unset',
            },
            '.Mui-selected': {
              color: 'var(--primary_main)',
              background: 'var(--white)',
              borderRadius: 12,
              padding: 12,
              margin: 1,
              fontWeight: 600,
              fontSize: 14,
              border: '1px solid var(--white)',
              boxShadow: 'var(--box-shadow_m)',
              '.MuiSvgIcon-root': {
                color: 'var(--primary_main)',
              },
            },
          },
          '&.light': {
            padding: 8,
            background: 'var(--white)',
            boxShadow: '0px 0px 5px 0px var(--shadowBlack08), 0px 0px 0px 1px var(--shadowBlack04)',
            borderRadius: 4,
            '.MuiTab-root': {
              padding: '8px 16px',
              lineHeight: '14px',
              color: 'var(--icons_primary)',
            },
            '.Mui-selected': {
              background: { xs: 'var(--white)' },
              fontWeight: 600,
              fontSize: 14,
              whiteSpace: 'nowrap',
              color: 'var(--cta_primary)',
            },
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: <Close />,
      },
      styleOverrides: {
        deleteIcon: {
          fontSize: 12,
          color: 'var(--icons_primary)',
        },
        root: ({ ownerState }) => ({
          height: 20,
          fontWeight: 600,
          borderRadius: 10,
          fontSize: 12,
          paddingLeft: 4,
          ...(ownerState.color === 'success' && {
            color: 'var(--green_dark)',
            background: 'var(--green_medium)',
            '.MuiSvgIcon-root': {
              color: 'var(--green_dark)',
            },
          }),
          ...(ownerState.color === 'default' && {
            color: 'var(--black_secondary)',
            background: 'var(--bg_primary)',
            '.MuiSvgIcon-root': {
              color: 'var(--black_secondary)',
            },
          }),
          ...(ownerState.color === 'error' && {
            color: 'var(--state_error)',
            background: 'var(--red_light)',
            '.MuiSvgIcon-root': {
              color: 'var(--state_error)',
            },
          }),
          ...(ownerState.color === 'warning' && {
            color: 'var(--warning_primary)',
            background: 'var(--yellow_light)',
          }),
          ...(ownerState.color === 'info' && {
            color: 'var(--blue_dark)',
            background: 'var(--blue_light)',
          }),
          ...(ownerState.color === 'primary' && {
            color: 'var(--primary_main)',
            background: 'var(--cta_bg)',
          }),
          ...(ownerState.color === 'secondary' && {
            color: 'var(--black)',
            background: 'var(--primary_10)',
            fontWeight: 400,
            fontSize: 14,
            height: 18,
            '.MuiSvgIcon-root': {
              color: 'var(--black_50)',
            },
            ' .MuiChip-deleteIcon': {
              margin: 0,
              ':hover': {
                color: 'var(--black_50)',
              },
            },
          }),
          '&.configStatus .MuiSvgIcon-root': {
            fontSize: 12,
          },
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'warning' && {
              color: 'var(--warning_text)',
              background: 'var(--warning_10)',
              border: 0,
              borderRadius: 0,
            }),
          ...(ownerState.label === timesheetStatuses.Recreated && {
            color: 'var(--olive_dark)',
            background: 'var(--olive_light)',
            '.MuiSvgIcon-root': {
              color: 'var(--olive_dark)',
            },
          }),
        }),
        icon: {
          fontSize: 8,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterTouchDelay: 0, // show instantly on touch
        leaveTouchDelay: 3000,
      },
      styleOverrides: {
        tooltip: {
          fontSize: 16,
          background: 'var(--purple_dark)',
          opacity: 0.9,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          padding: '8px 12px',
          borderRadius: 4,
          '&.menu-tooltip': {
            lineHeight: '21px',
            background: 'var(--informational_tertiary)',
            fontWeight: 400,
            color: 'var(--black_primary)',
            '.MuiTooltip-arrow': {
              ':before': {
                background: 'var(--informational_tertiary)',
              },
            },
          },
        },
        arrow: {
          ':before': {
            background: 'var(--purple_dark)',
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 0,
          width: 40,
          alignItems: 'center',
          overflow: 'visible',
          '&.customMedium': {
            width: 28,
            height: 16,
            padding: 0,
          },
        },
        track: {
          paddingInline: 0,
          opacity: 'unset',
          border: '1px solid var(--stroke_primary)',
          borderRadius: 40,
          backgroundColor: 'var(--white)',
          height: 20,
          width: 36,
          '.customMedium &': {
            height: 16,
            width: 28,
          },
        },
        switchBase: {
          paddingInline: 0,
          '.customMedium &': {
            height: 16,
            width: 16,
            padding: 0,
            '&.Mui-checked': {
              transform: 'translateX(12px)',
            },
          },
          color: 'var(--white)',
          '&.Mui-checked': {
            color: 'var(--white)',
            transform: 'translateX(16px)',
          },
          '&.Mui-checked+.MuiSwitch-track': {
            opacity: 'unset',
            border: '2px solid var(--primary_main)',
          },
          '&.Mui-checked .MuiSwitch-thumb': {
            border: '2px solid var(--primary_main)',
          },
          '&.Mui-checked.Mui-disabled+.MuiSwitch-track': {
            background: 'var(--cta_disabled)',
            borderColor: 'var(--cta_disabled)',
          },
          '&.Mui-disabled+.MuiSwitch-track': {
            opacity: 'unset',
            borderColor: 'var(--stroke_disabled)',
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: 'var(--white)',
            border: '1px solid var(--stroke_disabled)',
          },
          '&.Mui-checked.Mui-disabled .MuiSwitch-thumb': {
            color: 'var(--white)',
            border: '2px solid var(--cta_disabled)',
          },
        },
        thumb: {
          boxShadow: 'none',
          border: '1px solid var(--stroke_primary)',
          borderRadius: 40,
          height: 20,
          width: 20,
          '.customMedium &': {
            width: 16,
            height: 16,
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'lg',
      },
      styleOverrides: {
        paper: {
          padding: 24,
          boxShadow: 'none',
          borderRadius: 8,
          '.custom-icon': {
            marginRight: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            minWidth: 40,
            height: 40,
            '&.alert': {
              color: 'var(--state_error)',
              backgroundColor: 'var(--red_light)',
            },
            '&.warning': {
              color: 'var(--warning_primary)',
              backgroundColor: ' var(--yellow_light)',
            },
            '&.success': {
              color: 'var(--success)',
              backgroundColor: 'var(--success_10)',
            },
            '&.info': {
              color: 'var(--informational_primary)',
              backgroundColor: 'var(--informational_bg)',
            },
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 0,
          color: 'var(--black_primary)',
          fontWeight: 600,
          fontSize: 18,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          marginTop: 24,
          justifyContent: 'end',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          padding: 0,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          marginTop: 4,
          fontSize: 16,
          color: 'var(--black_secondary)',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: '4px 0 0 0',
          lineHeight: '16px',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          color: 'var(--text_primary)',
          boxShadow: 'none',
          background: 'var(--bg_secondary)',
          border: '1px solid var(--disabled_25)',
          ':before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          minHeight: 24,
        },
        content: {
          margin: 0,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-root': {
            padding: '6px 8px',
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
          },
          '& .MuiChip-label': {
            paddingLeft: 8,
            paddingRight: 8,
          },
          '& .MuiChip-root': {
            backgroundColor: 'var(--bg_secondary)',
            color: 'var(--text_primary)',
            fontWeight: 400,
            fontSize: 12,
          },
          '& .MuiChip-deleteIcon': {
            color: 'var(--icons_primary)',
            fontSize: 12,
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'var(--black_secondary)',
            fontWeight: 400,
            fontSize: 16,
          },
          '& .Mui-disabled .MuiInputBase-input::placeholder': {
            color: 'var(--text_disabled)',
            fontWeight: 400,
            fontSize: 16,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--stroke_secondary)',
          },
        },

        input: {
          padding: '6px 8px',
        },

        inputRoot: {
          // paddingRight: '10px !important',
        },

        endAdornment: {
          '.MuiIconButton-root': {
            // padding: 2,
          },
        },

        clearIndicator: {
          '& .MuiSvgIcon-root': {
            color: 'var(--icons_primary)',
          },
          '.Mui-disabled & .MuiSvgIcon-root': {
            color: 'var(--icons_disabled)',
          },
        },

        popupIndicator: {
          borderRadius: 0,
          '& .MuiSvgIcon-root': {
            color: 'var(--icons_primary)',
          },
          '.Mui-disabled & .MuiSvgIcon-root': {
            color: 'var(--icons_disabled)',
          },
        },
      },
      defaultProps: {
        popupIcon: <KeyboardArrowDownOutlined />,
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderColor: 'var(--cta_primary)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: 'var(--cta_primary)',
          borderColor: 'var(--cta_primary)',
          height: 32,
          fontSize: 14,
          textTransform: 'capitalize',
          '&.Mui-selected': {
            fontWeight: 600,
            color: 'var(--white)',
            backgroundColor: 'var(--cta_primary)',
            '&:hover': {
              backgroundColor: 'var(--cta_primary)',
            },
          },
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          padding: 0,
          '&.completed-step': {
            '.MuiStepLabel-root': {
              borderColor: 'var(--informational_secondary)',
            },
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: 20,
          '&.Mui-active': {
            color: 'var(--purple_dark)',
          },
          '&.Mui-completed': {
            color: 'var(--informational_secondary)',
            borderColor: 'var(--informational_secondary)',
          },
        },
        text: {
          fontWeight: 600,
          fontSize: 14,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          padding: 24,
          paddingTop: 16,
          paddingBottom: 16,
          border: 1,
          borderStyle: 'solid',
          borderColor: 'var(--purple_dark)',
          borderRadius: 8,
          '&.Mui-disabled': {
            borderColor: 'var(--informational_secondary)',
            '.MuiStepIcon-root': {
              border: '1px solid var(--informational_secondary)',
              borderRadius: '50%',
              color: 'transparent', // hide default icon fill
            },
            '.MuiStepIcon-text': {
              fill: 'var(--informational_secondary)',
            },
          },
        },
        label: {
          '&.Mui-active': {
            fontWeight: 600,
            color: 'var(--black_primary)',
          },
          '&.Mui-completed': {
            color: 'var(--black_secondary)',
          },
          '&.Mui-disabled': {
            color: 'var(--black_secondary)',
          },
        },
        iconContainer: {
          paddingRight: 16,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: 'var(--informational_secondary)',
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: 'var(--black_secondary)',
          backgroundColor: 'var(--white)',
          border: '1px solid var(--stroke_tertiary)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: 'var(--icons_disabled)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 12,
          alignItems: 'center',
          gap: 8,
        },
        message: {
          padding: 0,
          fontSize: 14,
        },
        icon: {
          marginRight: 0,
          padding: 0,
        },
        standardInfo: {
          backgroundColor: 'var(--informational_bg)',
          color: 'var(--informational_primary)',
        },
      },
    },
  },
});

// HCF Theme: Only primary color is different
export const hcfTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    primary: {
      main: '#31346F', // Example HCF primary color, change as needed
    },
  },
});

export default theme;
